/**
 * views/QRCorrectionWorklist.tsx
 * ───────────────────────────────
 * Customer-facing QR Correction Worklist panel.
 *
 * Mirrors the layout of the ExecutiveSummary panel (full-page takeover with a
 * "← Back to report" button), then groups flagged locations into:
 *   • Missing QR
 *   • Misplaced / Swapped QR
 *   • Damaged QR
 *
 * Each row shows the location code, confidence badge, evidence text, recommended
 * action, and up to 3 supporting photos (lazy-loaded with JWT auth).
 */

import { useEffect, useRef, useState } from 'react';
import { useAuthImage } from '@/hooks/useAuthImage';
import { ImageLightbox } from '@/components/ui/ImageLightbox';
import { buildPhotoUrl } from '@/api/inventory';
import {
  fetchQrWorklist,
  qrWorklistExportUrl,
  type QrWorklist,
  type QrWorklistEntry,
  type QrFaultCategory,
} from '@/api/qrWorklist';
import { apiClient } from '@/api/client';

// ── Confidence badge ──────────────────────────────────────────────────────────

function ConfBadge({ label }: { label: 'low' | 'medium' | 'high' }) {
  const cls = label === 'high' ? 'qrwl-conf--high' : label === 'medium' ? 'qrwl-conf--medium' : 'qrwl-conf--low';
  return <span className={`qrwl-conf-badge ${cls}`}>{label.charAt(0).toUpperCase() + label.slice(1)}</span>;
}

// ── Single location photo (lazy + auth) ───────────────────────────────────────

function LocationPhoto({ photoId }: { photoId: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { rootMargin: '100px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const src = useAuthImage(inView ? buildPhotoUrl(photoId) : null);
  const [expanded, setExpanded] = useState(false);

  return (
    <div ref={ref} className="qrwl-photo">
      {typeof src === 'string' ? (
        <img
          src={src}
          alt="location photo"
          className="qrwl-photo-img"
          style={{ cursor: 'zoom-in' }}
          role="button"
          tabIndex={0}
          onClick={() => setExpanded(true)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(true); }}
        />
      ) : (
        <div className="qrwl-photo-placeholder">{src === false ? '✕' : '⏳'}</div>
      )}
      {expanded && (
        <ImageLightbox photoId={photoId} onClose={() => setExpanded(false)} />
      )}
    </div>
  );
}

// ── Single worklist row ───────────────────────────────────────────────────────

function WorklistRow({ entry }: { entry: QrWorklistEntry }) {
  const isWrongQr = entry.category === 'wrong_qr_at_slot';
  return (
    <div className={`qrwl-row${isWrongQr ? ' qrwl-row--wrong-qr' : ''}`}>
      <div className="qrwl-row-header">
        <span className="qrwl-row-loc">{entry.location_code}</span>
        <ConfBadge label={entry.confidence_label} />
        {isWrongQr && entry.swapped_with && (
          <span className="qrwl-row-found-at">Neighbour QR: {entry.swapped_with}</span>
        )}
        {!isWrongQr && entry.swapped_with && (
          <span className="qrwl-row-swap">↔ {entry.swapped_with}</span>
        )}
      </div>

      <div className="qrwl-row-body">
        <div className="qrwl-row-text">
          <p className="qrwl-row-evidence">{entry.evidence}</p>
          <p className="qrwl-row-fix"><strong>Action:</strong> {entry.recommended_fix}</p>
        </div>

        {entry.photo_ids.length > 0 && (
          <div className="qrwl-photos">
            {entry.photo_ids.map((id) => (
              <LocationPhoto key={id} photoId={id} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Category section ──────────────────────────────────────────────────────────

const CATEGORY_ICONS: Record<QrFaultCategory, string> = {
  wrong_qr_at_slot: '🏷️',
  missing_qr: '🔍',
  misplaced_swapped_qr: '🔄',
  damaged_qr: '⚠️',
};

function CategorySection({
  label,
  category,
  entries,
}: {
  label: string;
  category: QrFaultCategory;
  entries: QrWorklistEntry[];
}) {
  if (entries.length === 0) return null;
  return (
    <section className={`qrwl-category qrwl-category--${category}`}>
      <h2 className="qrwl-category-title">
        <span className="qrwl-category-icon">{CATEGORY_ICONS[category]}</span>
        {label}
        <span className="qrwl-category-count">{entries.length}</span>
      </h2>
      <div className="qrwl-rows">
        {entries.map((e) => <WorklistRow key={e.location_code} entry={e} />)}
      </div>
    </section>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

interface Props {
  missionId: string;
  missionTitle: string;
  onBack: () => void;
}

export function QRCorrectionWorklist({ missionId, missionTitle, onBack }: Props) {
  const [data, setData] = useState<QrWorklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchQrWorklist(missionId)
      .then(setData)
      .catch(() => setError('Could not load QR correction worklist.'))
      .finally(() => setLoading(false));
  }, [missionId]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const url = qrWorklistExportUrl(missionId);
      const res = await apiClient.getRaw(url);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `QR_Correction_Worklist_${missionId}.xlsx`;
      a.click();
    } catch {
      // silent — user will notice the download didn't arrive
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="exec-summary qrwl">
      {/* ── Bar ── */}
      <div className="exec-summary-bar">
        <button className="btn btn--ghost btn--sm" onClick={onBack}>← Back to report</button>
        <div className="exec-summary-titles">
          <h1>QR Correction Worklist</h1>
          {missionTitle && <p>{missionTitle}</p>}
        </div>
        {data && (
          <button
            className="btn btn--primary btn--sm"
            onClick={handleDownload}
            disabled={downloading}
          >
            {downloading ? '⏳ Downloading…' : '↓ Download .xlsx'}
          </button>
        )}
      </div>

      {/* ── Body ── */}
      <div className="exec-summary-body qrwl-body">
        {loading && (
          <p className="qrwl-loading">Loading QR correction data…</p>
        )}

        {error && (
          <p className="qrwl-error">{error}</p>
        )}

        {data && !loading && (
          <>
            {/* Summary bar */}
            <div className="qrwl-summary">
              <div className="qrwl-summary-stat">
                <span className="qrwl-summary-num">{data.total_flagged}</span>
                <span className="qrwl-summary-label">locations flagged</span>
              </div>
              {data.wrong_qr_at_slot.length > 0 && (
                <div className="qrwl-summary-stat qrwl-summary-stat--wrong-qr">
                  <span className="qrwl-summary-num">{data.wrong_qr_at_slot.length}</span>
                  <span className="qrwl-summary-label">Wrong QR / Rework</span>
                </div>
              )}
              {data.missing_qr.length > 0 && (
                <div className="qrwl-summary-stat qrwl-summary-stat--missing">
                  <span className="qrwl-summary-num">{data.missing_qr.length}</span>
                  <span className="qrwl-summary-label">Missing QR</span>
                </div>
              )}
              {data.misplaced_swapped_qr.length > 0 && (
                <div className="qrwl-summary-stat qrwl-summary-stat--misplaced">
                  <span className="qrwl-summary-num">{data.misplaced_swapped_qr.length}</span>
                  <span className="qrwl-summary-label">Misplaced / Swapped</span>
                </div>
              )}
              {data.damaged_qr.length > 0 && (
                <div className="qrwl-summary-stat qrwl-summary-stat--damaged">
                  <span className="qrwl-summary-num">{data.damaged_qr.length}</span>
                  <span className="qrwl-summary-label">Damaged QR</span>
                </div>
              )}
            </div>

            {data.total_flagged === 0 ? (
              <p className="qrwl-empty">No QR issues found for this stocktake. ✓</p>
            ) : (
              <>
                <CategorySection
                  label="Wrong QR — Assignment Mismatch"
                  category="wrong_qr_at_slot"
                  entries={data.wrong_qr_at_slot}
                />
                <CategorySection
                  label="Missing QR"
                  category="missing_qr"
                  entries={data.missing_qr}
                />
                <CategorySection
                  label="Misplaced / Swapped QR"
                  category="misplaced_swapped_qr"
                  entries={data.misplaced_swapped_qr}
                />
                <CategorySection
                  label="Damaged QR"
                  category="damaged_qr"
                  entries={data.damaged_qr}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
